import { Inject, Injectable, Renderer2, RendererFactory2, inject } from '@angular/core';
import {
    REGEX_MATCH_EMAIL,
    REGEX_MATCH_URL,
    REGEX_MATCH_VIMEO,
    REGEX_MATCH_VIMEO_IFRAME,
    REGEX_MATCH_YOUTUBE,
    REGEX_MATCH_YOUTUBE2,
    REGEX_MATCH_YOUTUBE_IFRAME,
} from '@fi-shared/regexes';
import { TextEditorAttachmentType } from './fi-text-editor-attachment/text-editor-attachment-type';
import { findClosest, fiTextToHtml, isDefined } from '@fi-shared/utils';
import { ITextEditorLinkAttachment } from '@fi-shared/fi-text-editor/fi-text-editor';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { TAB } from '@angular/cdk/keycodes';
import { VIMEO_PLAYER_URL } from '@fi-shared/media-bank/fi-vimeo/fi-vimeo.service';
import { FiReplaceLinkTypes } from '@fi-shared/fi-directives/replace-with-links/replace-with-links.directive';
import { TextEditorTagUsersService } from '@fi-shared/fi-text-editor/fi-text-editor-helper-services/text-editor-tag-users.service';

@Injectable()
export class TextEditorService {
    protected renderer: Renderer2;
    private textEditorTagUsersService = inject(TextEditorTagUsersService);

    constructor(
        protected fiMediaStorageService: FiMediaStorageService,
        @Inject(RendererFactory2) rendererFactory2: RendererFactory2,
    ) {
        this.renderer = rendererFactory2.createRenderer(null, null);
    }

    private getText(node) {
        return node.nodeType === 3 ? node.textContent : node.innerText;
    }

    replaceUrl(node) {
        if (findClosest(node, 'a')) {
            return false;
        }

        const innerText = this.getText(node);
        const match = innerText.match(REGEX_MATCH_URL);
        if (!match) {
            return false;
        }

        let url = match[0];
        if (url.indexOf('http://') !== 0 && url.indexOf('https://') !== 0) {
            url = 'http://' + url;
        }

        this._replaceMatch(node, match[0], '<a href="' + url + '">' + match[0] + '</a>');
        return true;
    }

    replaceEmail(node) {
        if (findClosest(node, 'a')) {
            return false;
        }

        const innerText = this.getText(node);
        const match = innerText.match(REGEX_MATCH_EMAIL);
        if (!match) {
            return false;
        }

        this._replaceMatch(node, match[0], '<a href="mailto:' + match[0] + '">' + match[0] + '</a>');
        return true;
    }

    protected _replaceMatch(node, match, replaceWith) {
        const range = new Range();
        const innerText = this.getText(node);
        range.setStart(node, innerText.indexOf(match));
        range.setEnd(node, innerText.indexOf(match) + match.length);
        range.deleteContents();
        const div = document.createElement('div');
        div.innerHTML = replaceWith;
        range.insertNode(div.firstChild);
    }

    //This function picks a url and converts it to whatever is needed.
    replaceUrls(element: HTMLElement, replacementTypes: FiReplaceLinkTypes[]) {
        if (!replacementTypes) return;

        let isReplaced = false;
        if (
            contains(FiReplaceLinkTypes.Url, replacementTypes) ||
            contains(FiReplaceLinkTypes.Email, replacementTypes)
        ) {
            const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null);

            while (walker.nextNode()) {
                //This one converts links
                if (contains(FiReplaceLinkTypes.Url, replacementTypes)) {
                    this.replaceUrl(walker.currentNode);
                    isReplaced = true;
                }

                if (contains(FiReplaceLinkTypes.Email, replacementTypes)) {
                    this.replaceEmail(walker.currentNode);
                    isReplaced = true;
                }
            }
        }

        function contains<T>(item: T, arr: T[]) {
            return arr.indexOf(item) >= 0;
        }

        return isReplaced;
    }

    isEmbdedVideoUrlValid(url: string) {
        const res =
            REGEX_MATCH_YOUTUBE.test(url) ||
            REGEX_MATCH_YOUTUBE2.test(url) ||
            REGEX_MATCH_YOUTUBE_IFRAME.test(url) ||
            REGEX_MATCH_VIMEO.test(url) ||
            REGEX_MATCH_VIMEO_IFRAME.test(url);
        return res;
    }

    getEmbdedVideoUrl(url: string) {
        if (url && url.indexOf('www.youtube.com/embed') !== -1 && url.indexOf('iframe') === -1) {
            return url;
        }
        let YTmatches = url.match(REGEX_MATCH_YOUTUBE);
        if (YTmatches) {
            return YTmatches[0].replace(REGEX_MATCH_YOUTUBE, 'https://www.youtube.com/embed/$6').replace('&', '?');
        }
        YTmatches = url.match(REGEX_MATCH_YOUTUBE2);
        if (YTmatches) {
            return YTmatches[0].replace(REGEX_MATCH_YOUTUBE2, 'https://www.youtube.com/embed/$4').replace('&', '?');
        }
        YTmatches = url.match(REGEX_MATCH_YOUTUBE_IFRAME);
        if (YTmatches) {
            return YTmatches[0]
                .replace(REGEX_MATCH_YOUTUBE_IFRAME, 'https://www.youtube.com/embed/$3')
                .replace('&', '?');
        }

        let VIMEOmatches = url.match(REGEX_MATCH_VIMEO);
        if (VIMEOmatches) {
            return VIMEOmatches[0].replace(REGEX_MATCH_VIMEO, `${VIMEO_PLAYER_URL}/$5`).replace('&', '?');
        }
        VIMEOmatches = url.match(REGEX_MATCH_VIMEO_IFRAME);
        if (VIMEOmatches) {
            return VIMEOmatches[0].replace(REGEX_MATCH_VIMEO_IFRAME, `${VIMEO_PLAYER_URL}/$2`).replace('&', '?');
        }
        return 'about:blank';
    }

    updateAttachmentsFromContent(element: HTMLElement, attachments: ITextEditorLinkAttachment[]) {
        const existingUrls = attachments.map((item) => item.Url);
        const linkElements = element.querySelectorAll('a');
        const newUrls: string[] = [];
        // add new attachments
        linkElements.forEach((linkEl) => {
            const url = linkEl.getAttribute('href');

            /*if (this.isEmbdedVideoUrlValid(url)) {
                this.renderer.setAttribute(linkEl, 'ccm-text-item-embed-video', '');
            }*/

            let attachment: ITextEditorLinkAttachment;
            /*if (!isNullOrUndefined(linkEl.getAttribute('ccm-text-item-embed-video'))) {
                attachment = new TextEditorAttachment();
                attachment.LinkType = TextEditorAttachmentType.Youtube;
                attachment.Url = url;
                newUrls.push(attachment.Url);
            } else */ if (
                url.endsWith('.jpg') ||
                url.endsWith('.png') ||
                url.endsWith('.bmp') ||
                url.endsWith('.gif')
            ) {
                attachment = {
                    LinkType: TextEditorAttachmentType.Image,
                    Url: url,
                };
                newUrls.push(attachment.Url);
            }
            if (attachment && existingUrls.indexOf(attachment.Url) === -1) {
                attachments.push(attachment);
            }
        });
        // remove old attachments
        const attachmentsToRemove = attachments.filter(
            (attachment) =>
                /*attachment.LinkType === TextEditorAttachmentType.Youtube || */ attachment.LinkType ===
                    TextEditorAttachmentType.Image && newUrls.indexOf(attachment.Url) === -1,
        );
        attachmentsToRemove.forEach((attachmentToRemove) =>
            attachments.splice(attachments.indexOf(attachmentToRemove), 1),
        );
    }

    removeAttachmentFromContent(element: HTMLElement, attachment: ITextEditorLinkAttachment) {
        const linkElements = element.querySelectorAll('a');
        linkElements.forEach((linkEl) => {
            let url = linkEl.getAttribute('href');
            if (url === attachment.Url) {
                this.renderer.removeChild(linkEl.parentElement, linkEl);
            }
        });
    }

    removeEmptyLinesFromEnd(content: string) {
        let lastIndexOfEmptyString = content.lastIndexOf('<div><br></div>');
        while (lastIndexOfEmptyString !== -1 && lastIndexOfEmptyString === content.length - 15) {
            content = content.substring(0, content.length - 15);
            lastIndexOfEmptyString = content.lastIndexOf('<div><br></div>');
        }
        return content;
    }

    handleTabKeyPress($event: KeyboardEvent) {
        if ($event.keyCode === TAB && isDefined(this.getFocusedElement())) {
            const newFocusElement = $event.shiftKey ? this.removeInsertedSubList($event) : this.insertNewList($event);

            if (isDefined(newFocusElement)) this.setNewSelection(newFocusElement);
        }
    }

    handleUserTagBackspace($event: KeyboardEvent, editorElement: HTMLElement, onContentChanged?: () => void): void {
        this.textEditorTagUsersService.handleUserTagBackspace($event, editorElement, onContentChanged);
    }

    private getAnchorNodeData(): { innerHTML: string; isCaretAtTheStart: boolean } {
        const selection = window.getSelection();
        let innerHTML = '';
        if (!selection.anchorOffset)
            innerHTML =
                selection.anchorNode.nodeType === Node.TEXT_NODE
                    ? selection.anchorNode.textContent
                    : (selection.anchorNode as HTMLElement).innerHTML;

        return {
            innerHTML,
            isCaretAtTheStart: !selection.anchorOffset,
        };
    }

    private getFocusedListItemHTMLElement() {
        // note: after inserting empty rows by pressing Enter key in document for some reason double span element is inserted
        // it happens not all the time https://chainformation.atlassian.net/browse/FI-3926?focusedCommentId=47453
        // (<div><span><span><br></span></span></div>)
        const focusedElement: HTMLElement = this.getFocusedElement();
        if (!focusedElement) {
            return;
        }

        if (focusedElement.nodeName.toLowerCase() === 'li') return focusedElement;

        if (focusedElement.nodeName.toLowerCase() === 'span') {
            const liElement: HTMLElement = findClosest(focusedElement, 'LI', '.text-editor');
            if (isDefined(liElement)) return liElement;
        }
    }

    private insertNewList($event: KeyboardEvent) {
        const focusedNodeElement: HTMLElement = this.getFocusedListItemHTMLElement();
        if (!focusedNodeElement) {
            return;
        }
        const isListItemElement: boolean = focusedNodeElement.nodeName.toLowerCase() === 'li';
        const isSpanInListItemElement: boolean =
            focusedNodeElement.nodeName.toLowerCase() === 'span' &&
            focusedNodeElement.parentNode.nodeName.toLowerCase() === 'li';
        const anchorNodeData = this.getAnchorNodeData();

        if (isListItemElement || isSpanInListItemElement) {
            $event.preventDefault();
            const parentElement = isListItemElement
                ? focusedNodeElement.parentElement
                : focusedNodeElement.parentElement.parentElement;
            const mainNode = isListItemElement ? focusedNodeElement : focusedNodeElement.parentElement;

            if (
                parentElement.children.length &&
                parentElement.firstChild !== mainNode &&
                anchorNodeData.isCaretAtTheStart
            ) {
                const childIndex = Array.prototype.indexOf.call(parentElement.children, mainNode);
                const childrenArray = Array.from(parentElement.children);
                const newListItemElement = document.createElement('li');
                newListItemElement.innerHTML = anchorNodeData.innerHTML;

                if (childIndex !== childrenArray.length - 1) {
                    if (
                        this.isNotFirstAndNotLastItem(childrenArray, childIndex) &&
                        this.isCloseSiblingLists(childrenArray, childIndex, parentElement.nodeName.toLowerCase())
                    ) {
                        childrenArray[childIndex - 1].appendChild(newListItemElement);

                        Array.from(childrenArray[childIndex + 1].children).forEach((child) => {
                            const tempChild = document.createElement('li');
                            tempChild.innerText = (child as HTMLElement).innerText;
                            childrenArray[childIndex - 1].appendChild(tempChild);
                        });
                        parentElement.removeChild(childrenArray[childIndex + 1]);
                        parentElement.removeChild(childrenArray[childIndex]);
                        return newListItemElement;
                    }

                    if (this.isListElement(childrenArray[childIndex + 1] as HTMLElement)) {
                        parentElement.removeChild(mainNode);
                        childrenArray[childIndex + 1].insertBefore(
                            newListItemElement,
                            childrenArray[childIndex + 1].firstChild,
                        );
                        return newListItemElement;
                    }
                }

                if (childIndex && this.isListElement(childrenArray[childIndex - 1] as HTMLElement)) {
                    parentElement.removeChild(mainNode);
                    childrenArray[childIndex - 1].appendChild(newListItemElement);
                    return newListItemElement;
                }

                const newListElement = document.createElement(parentElement.nodeName.toLowerCase());
                newListElement.appendChild(newListItemElement);
                parentElement.replaceChild(newListElement, mainNode);
                return newListItemElement;
            }
            return;
        }
        return;
    }

    private removeInsertedSubList($event: KeyboardEvent) {
        $event.preventDefault();
        const focusedNodeElement: HTMLElement = this.getFocusedListItemHTMLElement();
        const anchorNodeData = this.getAnchorNodeData();
        if (isDefined(focusedNodeElement) && anchorNodeData.isCaretAtTheStart) {
            const parentElement = focusedNodeElement.parentElement;
            const childIndex = Array.prototype.indexOf.call(parentElement.children, focusedNodeElement);
            const childrenArray = Array.from(parentElement.children);
            const newListItemElement = document.createElement('li');
            newListItemElement.innerHTML = anchorNodeData.innerHTML;
            const newListElement = document.createElement(parentElement.nodeName.toLowerCase());

            if (this.isListElement(parentElement)) {
                if (!this.isListElement(parentElement.parentElement)) {
                    if (childrenArray.length === 1) {
                        parentElement.parentElement.removeChild(parentElement);
                        return;
                    }
                    if (childIndex === childrenArray.length - 1 || (!childIndex && childrenArray.length !== 1)) {
                        parentElement.removeChild(focusedNodeElement);
                        return;
                    }

                    if (this.isNotFirstAndNotLastItem(childrenArray, childIndex)) {
                        childrenArray.slice(childIndex + 1).forEach((child) => {
                            const tempChild = document.createElement('li');
                            tempChild.innerText = (child as HTMLElement).innerText;
                            newListElement.appendChild(tempChild);
                            parentElement.removeChild(child);
                        });
                        parentElement.removeChild(focusedNodeElement);

                        const newDivElement = document.createElement('div');
                        newDivElement.innerHTML = '<br>';
                        parentElement.parentElement.appendChild(newDivElement);
                        parentElement.parentElement.appendChild(newListElement);
                        return newDivElement;
                    }
                }

                const parentChildIndex = Array.prototype.indexOf.call(
                    parentElement.parentElement.children,
                    parentElement,
                );
                const parentChildrenArray = Array.from(parentElement.parentElement.children);

                if (childrenArray.length === 1) {
                    if (
                        !this.isListElement(parentChildrenArray[parentChildIndex - 1] as HTMLElement) &&
                        !this.isListElement(parentChildrenArray[parentChildIndex + 1] as HTMLElement)
                    ) {
                        parentElement.parentElement.insertBefore(newListItemElement, parentElement);
                        parentElement.parentElement.removeChild(parentElement);
                        return newListItemElement;
                    }

                    parentElement.parentElement.appendChild(newListItemElement);
                    parentElement.parentElement.removeChild(parentElement);
                    return newListItemElement;
                }

                if (!childIndex) {
                    parentElement.removeChild(focusedNodeElement);
                    parentElement.parentElement.insertBefore(newListItemElement, parentElement);
                    return newListItemElement;
                }

                if (childIndex === childrenArray.length - 1) {
                    parentElement.removeChild(focusedNodeElement);
                    if (parentChildIndex !== parentChildrenArray.length - 1) {
                        parentElement.parentElement.insertBefore(
                            newListItemElement,
                            parentChildrenArray[parentChildIndex + 1],
                        );
                        return newListItemElement;
                    }

                    parentElement.parentElement.appendChild(newListItemElement);
                    return newListItemElement;
                }
                parentElement.removeChild(focusedNodeElement);

                childrenArray.slice(childIndex + 1).forEach((child) => {
                    const tempChild = document.createElement('li');
                    tempChild.innerText = (child as HTMLElement).innerText;
                    newListElement.appendChild(tempChild);
                    parentElement.removeChild(child);
                });

                if (this.isCloseSiblingsListItems(childrenArray, childIndex)) {
                    const tempListItem = document.createElement('li');
                    tempListItem.innerHTML = anchorNodeData.innerHTML;
                    parentElement.parentElement.insertBefore(tempListItem, parentElement.nextElementSibling);
                    parentElement.parentElement.insertBefore(newListElement, tempListItem.nextElementSibling);
                    return tempListItem;
                }

                parentElement.parentElement.appendChild(newListItemElement);
                parentElement.parentElement.appendChild(newListElement);
                return newListItemElement;
            }
            return;
        }
        return;
    }

    private isCloseSiblingsListItems(childrenArray: Element[], childIndex: number): boolean {
        return (
            childrenArray[childIndex - 1].nodeName.toLowerCase() === 'li' &&
            childrenArray[childIndex + 1].nodeName.toLowerCase() === 'li'
        );
    }
    private isNotFirstAndNotLastItem(childrenArray: Element[], childIndex): boolean {
        return childIndex !== 0 && childIndex !== childrenArray.length - 1;
    }
    private isCloseSiblingLists(childrenArray: Element[], childIndex: number, listNodeName: string): boolean {
        return (
            childrenArray[childIndex + 1].nodeName.toLowerCase() === listNodeName &&
            childrenArray[childIndex - 1].nodeName.toLowerCase() === listNodeName
        );
    }
    private isListElement(element: HTMLElement): boolean {
        return isDefined(element)
            ? element.nodeName.toLowerCase() === 'ol' || element.nodeName.toLowerCase() === 'ul'
            : false;
    }

    private setNewSelection(element: HTMLElement) {
        document.getSelection().setBaseAndExtent(element, 0, element, 0);
    }

    getFocusedElement() {
        const node: HTMLElement = <HTMLElement>document.getSelection().anchorNode;
        return node && node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
    }
}

export function blurActiveElement() {
    const ae = window.document.activeElement;
    if (ae instanceof HTMLElement) {
        ae.blur();
    }
}
